// cta.tsx — site-wide call-to-action primitives.
//
// Two reusable components are exported here:
//
//   1. BrandPill      — uniform pill button used in Subscribe rows
//   2. FramedCallout  — bordered container with a gold corner accent,
//                       used for "invitation" or "uplift" callouts
//
// Both are intentionally small. BrandPill is shape-only — colour carries
// the platform identity. FramedCallout is decoration-only — content
// composition is up to the consumer. Reuse over re-implementing.

import { forwardRef, type CSSProperties, type ReactNode } from "react";

/* ── BrandPill ─────────────────────────────────────────── */

/**
 * Uniform pill button for subscribe / follow / join CTAs.
 *
 * The shape — 200px min-width, 32px tall, 16px radius — is identical
 * across every instance. **Colour is the only differentiator.** No
 * iconography sits inside the pill; the brand colour is the affordance.
 *
 * Recommended brand colours used elsewhere on the site:
 * - LinkedIn        `#0A66C2`
 * - Substack        `#FF6719`
 * - Reddit          `#FF4500`
 * - Medium          `#000000`
 * - TamedAutonomy   `#3a5a40` (site evergreen; sibling property)
 *
 * When `disabled` is true the pill renders as a non-clickable `<span>`
 * with a muted background. Use the `disabledLabel` for the visible
 * tagline (e.g. "soon") so the button still reads as aspirational
 * rather than broken.
 *
 * Opens in a new tab by default (target="_blank", noopener noreferrer).
 *
 * @example
 * <BrandPill
 *   href="https://kinetictrust.substack.com/"
 *   label="Follow on Substack"
 *   background="#FF6719"
 * />
 *
 * @example  Disabled / coming-soon variant
 * <BrandPill
 *   disabled
 *   label="Publish your work here"
 *   disabledLabel="soon"
 *   title="A future hosted-publishing surface."
 * />
 */
export function BrandPill({
  href,
  label,
  background,
  color,
  disabled,
  disabledLabel,
  title,
  style,
}: {
  /** Required when not disabled. Ignored if disabled. */
  href?: string;
  label: string;
  /** Required when not disabled. Ignored if disabled. */
  background?: string;
  /** Label color — defaults to white. Set per-pill when the brand
   *  background is too light for white text (WCAG 4.5:1). */
  color?: string;
  /** When true, renders as a muted, non-clickable pill. */
  disabled?: boolean;
  /** Small parenthetical that surfaces on the disabled variant
   *  (e.g. "soon", "coming"). Ignored when not disabled. */
  disabledLabel?: string;
  /** Optional native tooltip. Useful for explaining a disabled pill. */
  title?: string;
  /** Optional style overrides — merged onto the base pill style. */
  style?: CSSProperties;
}) {
  const baseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px",
    minWidth: 200,
    height: 32,
    borderRadius: 16,
    color: color ?? "#ffffff",
    textDecoration: "none",
    fontFamily: '"Inter", "SF Pro Text", Helvetica, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.2,
  };

  if (disabled) {
    return (
      <span
        title={title}
        aria-disabled="true"
        style={{
          ...baseStyle,
          background: "var(--ink-3)",
          opacity: 0.55,
          cursor: "not-allowed",
          ...style,
        }}
      >
        {label}
        {disabledLabel && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 10,
              fontWeight: 400,
              opacity: 0.85,
              letterSpacing: 0.4,
            }}
          >
            ({disabledLabel})
          </span>
        )}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      style={{
        ...baseStyle,
        background,
        ...style,
      }}
    >
      {label}
    </a>
  );
}

/* ── Shared engagement pills ───────────────────────────── */

/**
 * Canonical list of subscribe / follow / join surfaces. Single source of
 * truth for URLs, labels, and brand colours. Each surface carries its
 * own character — see the per-platform `note` for the editorial role
 * each plays — but pieces end up where they end up; readers pick the
 * rhythm that fits.
 *
 * The disabled trailing pill ("Publish your work here") is a future
 * hosted-publishing surface and renders muted across every consumer.
 */
export type EngagementPill =
  | {
      kind: "live";
      href: string;
      label: string;
      background: string;
      /** Label color override for light brand backgrounds (WCAG). */
      color?: string;
      /** Editorial role — used in copy, not rendered inside the pill. */
      note: string;
    }
  | {
      kind: "soon";
      label: string;
      disabledLabel: string;
      title: string;
    };

export const ENGAGEMENT_PILLS: readonly EngagementPill[] = [
  {
    kind: "live",
    href: "https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7403491389743022080",
    label: "Subscribe on LinkedIn",
    background: "#0A66C2",
    note: "short-form Digital Gravity for Enterprise",
  },
  {
    kind: "live",
    href: "https://kinetictrust.substack.com/",
    label: "Follow on Substack",
    background: "#FF6719",
    // Substack orange carries only 2.9:1 under white text; dark ink
    // reads at 6.6:1 and leaves the brand background untouched.
    color: "#0e0e10",
    note: "Kinetic Trust Protocol details",
  },
  {
    kind: "live",
    href: "https://www.reddit.com/r/DigitalPhysics/",
    label: "Join r/DigitalPhysics",
    background: "#FF4500",
    // Reddit orangered is 3.4:1 under white text; dark ink is 5.6:1.
    color: "#0e0e10",
    note: "open discussion, all of the above",
  },
  {
    kind: "live",
    href: "https://medium.com/@chrisperkins505",
    label: "Follow on Medium",
    background: "#000000",
    note: "lyrical essays",
  },
  {
    kind: "live",
    href: "https://tamedautonomy.ai/",
    label: "Explore TamedAutonomy",
    background: "#3a5a40",
    note: "sibling project, complementary frame",
  },
  {
    kind: "soon",
    label: "Publish your work here",
    disabledLabel: "soon",
    title:
      "A future hosted-publishing surface for collaborators. Coming soon.",
  },
];

/**
 * Renders the canonical six-pill engagement row. Pages compose their own
 * copy (eyebrow, heading, body) above this row; the pills themselves are
 * a single shared block. */
export function EngagementPillRow({ style }: { style?: CSSProperties }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
        ...style,
      }}
    >
      {ENGAGEMENT_PILLS.map((p) =>
        p.kind === "live" ? (
          <BrandPill
            key={p.label}
            href={p.href}
            label={p.label}
            background={p.background}
            color={p.color}
            title={p.note}
          />
        ) : (
          <BrandPill
            key={p.label}
            disabled
            label={p.label}
            disabledLabel={p.disabledLabel}
            title={p.title}
          />
        )
      )}
    </div>
  );
}

/* ── FramedCallout ─────────────────────────────────────── */

/**
 * Bordered container with a gold corner accent. The "invitation card"
 * decoration extracted from the Home page so it can be reused across
 * the site as a delight / uplift surface.
 *
 * Decoration:
 * - 1px solid `var(--ink)` border around the panel
 * - `var(--panel)` background
 * - Soft drop shadow (`var(--shadow-soft)`)
 * - Decorative gold radial gradient in the top-right corner
 *   (`var(--accent-2-glow)` fading to transparent over 70%)
 *
 * Content composition is the consumer's choice — children render inside
 * the framed container with `position: relative` so absolute-positioned
 * decoration sits above background but below text.
 *
 * @example  Simple invitation
 * <FramedCallout>
 *   <T>for peer researchers</T>
 *   <H size={2}>Convergent thinkers welcome.</H>
 *   <p>...</p>
 *   <Btn primary href="mailto:...">Reach out →</Btn>
 * </FramedCallout>
 *
 * @example  Two-column composition
 * <FramedCallout>
 *   <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
 *     <div>...left column...</div>
 *     <div>...right column...</div>
 *   </div>
 * </FramedCallout>
 */
export const FramedCallout = forwardRef<
  HTMLElement,
  {
    children: ReactNode;
    /** Optional className for layout-side modifiers (e.g. mobile responsive
     *  classes that already exist in the global CSS). */
    className?: string;
    /** Optional style overrides — merged onto the base container. */
    style?: CSSProperties;
  }
>(function FramedCallout({ children, className, style }, ref) {
  return (
    <section
      ref={ref}
      className={className}
      style={{
        position: "relative",
        padding: "32px 36px",
        border: "1px solid var(--ink)",
        background: "var(--panel)",
        boxShadow: "var(--shadow-soft)",
        overflow: "hidden",
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 80,
          height: 80,
          background:
            "radial-gradient(circle at top right, var(--accent-2-glow), transparent 70%)",
          pointerEvents: "none",
        }}
      />
      {children}
    </section>
  );
});
